import { UzCombobox } from '@utazzo/ui';

import { usePickerOptions } from './usePickerOptions';

import * as hotelsApi from '@/features/hotels/hotels.api';

interface HotelRoomPickerProps {
  label?: string;
  /** Required - pickers HotelRoom rows from this specific Hotel only. */
  hotelId: string;
  value: string | null;
  onValueChange: (value: string | null) => void;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  hint?: string;
}

export const HotelRoomPicker = ({
  label = 'Hotel room',
  hotelId,
  value,
  onValueChange,
  required,
  disabled,
  error,
  hint,
}: HotelRoomPickerProps): React.JSX.Element => {
  const { options, loading, setSearch } = usePickerOptions(
    {
      cacheKey: `picker.hotelRooms.${hotelId}`,
      fetch: async () => {
        const rows = await hotelsApi.listHotelRooms(hotelId);
        return rows;
      },
      fetchOne: async (id) => {
        try {
          const rows = await hotelsApi.listHotelRooms(hotelId);
          return rows.find((r) => r.id === id) ?? null;
        } catch {
          return null;
        }
      },
      toOption: (r) => ({
        value: r.id,
        label: r.name,
        description: r.roomTypeName ?? r.mealPlanCode ?? undefined,
      }),
    },
    value,
  );

  // HotelRoom list endpoint doesn't take a search param - filter locally.
  return (
    <UzCombobox
      label={label}
      placeholder="Select a room"
      searchPlaceholder="Search rooms..."
      value={value ?? ''}
      onValueChange={(v) => onValueChange(v || null)}
      onSearchChange={setSearch}
      options={options}
      loading={loading}
      required={required}
      disabled={disabled}
      error={error}
      hint={hint}
    />
  );
};
